// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/SearchResultsSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SearchResultsSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const sr = data?.searchResults;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = sr && (sr.foundOnPrimarySite !== undefined || sr.foundOnManufacturerSite !== undefined || sr.confidenceLevel);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['search-results']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'search-results',
          label: (
            <Flex align="center" gap={8}>
              <SearchOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('search_results')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              <Descriptions.Item label={translateX('confidence_level')}>
                <Tag color={sr.confidenceLevel === 'high' ? 'green' : sr.confidenceLevel === 'medium' ? 'orange' : 'red'}>
                  {translateX(sr.confidenceLevel || 'unknown')?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={translateX('found_on_manufacturer_site')}>
                {sr.foundOnManufacturerSite ? (
                  <Tag color="green">{translateX('yes')}</Tag>
                ) : (
                  <Tag color="default">{translateX('no')}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('ean_matched')}>
                {sr.eanMatched ? (
                  <Tag color="green">{translateX('yes')}</Tag>
                ) : (
                  <Tag color="default">{translateX('no')}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('image_source')}>
                {sr.imageSource ? (
                  <Tag color="blue">{translateX(sr.imageSource)}</Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              {sr.sources?.length > 0 && (
                <Descriptions.Item label={translateX('sources')}>
                  <Flex vertical gap={4}>
                    {sr.sources.map((source, idx) => (
                      <a key={idx} href={source.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                        {translateX(source.type)}: {source.url}
                      </a>
                    ))}
                  </Flex>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SearchResultsSection;
